import ClientComponent from '@/components/ClientComponent/ClientComponent';
import { BlogPost } from '@/typings/blog';
import { Locale } from '@/typings/locale';
import { StreamableResponse } from '@/typings/streamable';
import { Tag } from '@/typings/tag';
import axios from '@/utils/axios';
import { firebaseAdmin } from '@/utils/firebaseAdmin';
import BlogDetailView from '@/views/BlogDetail/BlogDetail';
import { GetStaticPaths, GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';

type BlogDetailProps = {
  blogPost: BlogPost;
  tags: Tag[];
  allTags: Tag[];
  locale: Locale;
  videoUrl: string | null;
};

function BlogDetail({ blogPost, tags, allTags, videoUrl }: BlogDetailProps) {
  return (
    <>
      <NextSeo
        title={blogPost.title}
        description={blogPost.shortDescription}
        openGraph={{
          title: blogPost.title,
          description: blogPost.shortDescription,
          siteName: 'Enoteca Mundi',
          type: 'article',
          url: `https://enotecamundi.com.br/blog/post/${blogPost.slug}`,
          article: {
            tags: tags.map((tag) => tag.title),
            publishedTime: blogPost.publishedAt?.toString(),
            modifiedTime: blogPost.updatedAt?.toString(),
          },
          images: [
            {
              url: blogPost.banner,
              width: 1200,
              height: 1200,
            },
          ],
        }}
      />
      <ClientComponent>
        <BlogDetailView blogPost={blogPost} tags={tags} allTags={allTags} videoUrl={videoUrl} />
      </ClientComponent>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const db = firebaseAdmin.firestore();
  const blogPostsCollection = db.collection('blogPosts');
  const { docs } = await blogPostsCollection.where('deletedAt', '==', null).get();
  const blogPosts = docs.map((doc) => ({ ...doc.data(), id: doc.id })) as BlogPost[];

  const paths = blogPosts.map((blogPost: BlogPost) => {
    return {
      params: { slug: blogPost.slug },
    };
  });
  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = context.params?.slug;

  if (!slug) {
    return {
      notFound: true,
    };
  }

  const db = firebaseAdmin.firestore();
  const blogPostsCollection = db.collection('blogPosts');
  const { docs } = await blogPostsCollection
    .where('slug', '==', slug)
    .where('deletedAt', '==', null)
    .get();

  const publishedAt = docs[0] ? docs[0].data().publishedAt : null;
  const updatedAt = docs[0] ? docs[0].data().updatedAt : null;

  const blogPost = docs[0]
    ? ({
        ...docs[0].data(),
        id: docs[0].id,
        publishedAt: publishedAt ? publishedAt.toDate() : null,
        updatedAt: updatedAt ? updatedAt.toDate() : null,
      } as BlogPost)
    : null;

  if (!blogPost || !blogPost.isPublished) {
    return {
      notFound: true,
    };
  }

  let tags: Tag[] = [];

  const tagsCollection = db.collection('tags');
  const { docs: tagDocs } = await tagsCollection.where('deletedAt', '==', null).get();

  tags = tagDocs.map((doc) => ({ ...doc.data(), id: doc.id })) as Tag[];

  let videoUrl: string | null = null;

  if (!blogPost.noVideo && blogPost.video?.streamableUrl) {
    const videoId = blogPost.video.streamableUrl.split('/').pop();
    const { data } = await axios<StreamableResponse>(
      `https://api.streamable.com/videos/${videoId}`,
      {
        method: 'GET',
      },
    );
    videoUrl = data.embed_code;
  }

  return {
    props: {
      blogPost: JSON.parse(JSON.stringify(blogPost)),
      tags: tags
        ? JSON.parse(JSON.stringify(tags.filter((tag) => blogPost.tagIds?.includes(tag.id))))
        : [],
      allTags: tags ? JSON.parse(JSON.stringify(tags)) : [],
      locale: context.locale,
      videoUrl: videoUrl || null,
    },
    revalidate: 2592000,
  };
};

export default BlogDetail;
